import { useState } from 'react'

// Small "🔗 Copy link" button that copies the current page's URL to the
// clipboard so users can paste it in Slack / email / notes.  Emits a 2-sec
// "✓ Copied" flash on success.  Uses the modern Clipboard API which requires
// a secure context (https or localhost) — falls back silently to a document
// selection hack on non-secure contexts so it still works during dev on
// http.  The Electron desktop app runs in a secure context so the modern
// API works there too.
//
// Props:
//   - url (optional)     : the URL to copy.  Defaults to `window.location.href`.
//   - label (optional)   : the button label.  Defaults to '🔗 Copy link'.
//   - copiedLabel (opt)  : the "success" label.  Defaults to '✓ Copied'.
//   - title (optional)   : tooltip.  Defaults to a description.
//   - className (opt)    : override styling.
export default function CopyLinkButton({
  url,
  label = '🔗 Copy link',
  copiedLabel = '✓ Copied',
  title = 'Copy this page\'s URL to your clipboard',
  className,
}) {
  const [copied, setCopied] = useState(false)

  const doCopy = async () => {
    const target = url || (typeof window !== 'undefined' ? window.location.href : '')
    if (!target) return
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(target)
      } else {
        // Fallback for non-secure contexts — create a temp textarea, select,
        // execCommand.  Deprecated but still works everywhere.  Very rare
        // path (production is https so we hit the modern API).
        const ta = document.createElement('textarea')
        ta.value = target
        ta.style.position = 'fixed'
        ta.style.left = '-9999px'
        document.body.appendChild(ta)
        ta.focus()
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard blocked (probably a permission-policy or an insecure origin
      // without the fallback path).  Fall silently — the user can right-click
      // the URL bar as a manual escape hatch.
    }
  }

  return (
    <button
      type="button"
      onClick={doCopy}
      title={title}
      className={
        className ||
        `text-xs px-2 py-1 rounded border transition-colors ${
          copied
            ? 'bg-green-50 border-green-200 text-green-700'
            : 'border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
        }`
      }>
      {copied ? copiedLabel : label}
    </button>
  )
}
